import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import { GoogleMap, useJsApiLoader, StandaloneSearchBox, Marker } from '@react-google-maps/api';
import { useTranslation } from 'react-i18next';
import { isRTL } from 'core/services/language';
import { Skeleton } from 'antd';

interface MapComponentProps {
	onSelect?: (lat: number, lng: number, address: string) => void;
	lat?: number;
	lng?: number;
	disabled?: boolean;
}

const containerStyle = {
	height: '500px',
	borderRadius: 'var(--radius)',
};

const libraries: 'places'[] = ['places'];

const MapComponent: FunctionComponent<MapComponentProps> = ({ onSelect, lat, lng, disabled }) => {
	const { t } = useTranslation();

	const searchBoxRef = useRef<google.maps.places.SearchBox | null>(null);
	const mapRef = useRef<google.maps.Map | null>(null);
	const geocoderRef = useRef<google.maps.Geocoder | null>(null);

	const [markerPosition, setMarkerPosition] = useState<google.maps.LatLngLiteral | null>(null);
	const [center, setCenter] = useState<google.maps.LatLngLiteral>({ lat: 24.7081241, lng: 46.6859019 });

	const { isLoaded } = useJsApiLoader({
		id: 'google-map-script',
		googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_KEY || '',
		libraries,
		region: 'SA',
	});

	useEffect(() => {
		if (lat && lng) {
			setMarkerPosition({ lat, lng });
			setCenter({ lat, lng });
			mapRef.current?.panTo({ lat, lng });
		}
	}, [lat, lng]);

	useEffect(() => {
		if (navigator.geolocation && !lat && !lng) {
			navigator.geolocation.getCurrentPosition(
				(position) => {
					const { latitude, longitude } = position.coords;
					const location = { lat: latitude, lng: longitude };

					setMarkerPosition(location);
					mapRef.current?.panTo(location);
					getAddressFromCoords(latitude, longitude);
				},
				(error) => {
					console.error('Geolocation error:', error);
				},
			);
		}
	}, [isLoaded]);

	const getAddressFromCoords = (lat: number, lng: number) => {
		if (!geocoderRef.current) return;
		geocoderRef.current.geocode({ location: { lat, lng } }, (results, status) => {
			if (status === 'OK' && results && results.length > 0) {
				onSelect?.(lat, lng, results[0].formatted_address);
			}
		});
	};

	const onPlacesChanged = () => {
		const places = searchBoxRef.current?.getPlaces();
		if (!places || places.length === 0) return;

		const place = places[0];
		if (!place.geometry?.location) return;

		const location = {
			lat: place.geometry.location.lat(),
			lng: place.geometry.location.lng(),
		};

		setMarkerPosition(location);
		mapRef.current?.panTo(location);
		getAddressFromCoords(location.lat, location.lng);
	};

	return isLoaded ? (
		<GoogleMap
			mapContainerStyle={containerStyle}
			center={center}
			zoom={15}
			onLoad={(map) => {
				mapRef.current = map;
				geocoderRef.current = new window.google.maps.Geocoder();
			}}
			onClick={(e) => {
				if (!disabled) {
					const lat = e.latLng?.lat();
					const lng = e.latLng?.lng();
					if (lat && lng) {
						setMarkerPosition({ lat, lng });
						getAddressFromCoords(lat, lng);
						mapRef.current?.panTo({ lat, lng });
					}
				}
			}}
			options={{
				streetViewControl: false,
				fullscreenControl: false,
				scrollwheel: true,
				controlSize: 30,
				draggableCursor: 'pointer',
			}}>
			{!disabled && (
				<StandaloneSearchBox onLoad={(ref) => (searchBoxRef.current = ref)} onPlacesChanged={onPlacesChanged}>
					<input
						type="text"
						autoComplete="off"
						placeholder={t('ACTIONS.SEARCH_FOR_PLACE')}
						style={{
							boxSizing: 'border-box',
							border: 'none',
							width: '400px',
							maxWidth: '100%',
							height: '40px',
							padding: '5px 10px',
							borderRadius: 'var(--radius)',
							boxShadow: '0 2px 6px rgba(0, 0, 0, 0.3)',
							fontSize: '15px',
							outline: 'none',
							textOverflow: 'ellipsis',
							position: 'absolute',
							insetInlineStart: isRTL() ? '10px' : 'unset',
							insetInlineEnd: isRTL() ? 'unset' : '10px',
							top: '10px',
						}}
					/>
				</StandaloneSearchBox>
			)}

			{markerPosition && <Marker position={markerPosition} />}
		</GoogleMap>
	) : (
		<Skeleton.Button block active style={{ height: '500px', borderRadius: 'var(--radius)' }} />
	);
};

export default React.memo(MapComponent);
